import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LoadingSplash = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-lime border-t-transparent rounded-full animate-spin" />
  </div>
);

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSplash />;
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ returnTo: location.pathname }} replace />;
  }
  return children;
};

/**
 * AdminRoute — hardened after a pen-test finding.
 *
 * `isAdmin` is set by AuthContext ONLY when the server confirms admin status
 * via `GET /api/auth/verify-roles`. An attacker MITM'ing the login response
 * to flip `user.role` cannot flip that flag because the verification uses the
 * signed JWT cookie (which they cannot forge) and returns authoritative role
 * booleans rather than trusting the client-side login-response body.
 *
 * `adminVerifying` covers the tiny window between login and the verify RTT.
 */
export const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, testMode, adminVerifying, loading } = useAuth();

  if (loading || adminVerifying) return <LoadingSplash />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin && !testMode?.active) return <Navigate to="/" replace />;
  return children;
};

export const ArtistRoute = ({ children }) => {
  const { isAuthenticated, isArtist, adminVerifying, loading } = useAuth();
  if (loading || adminVerifying) return <LoadingSplash />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isArtist) return <Navigate to="/" replace />;
  return children;
};

export const AgentRoute = ({ children }) => {
  const { isAuthenticated, isAgent, isAdmin, adminVerifying, loading } = useAuth();
  if (loading || adminVerifying) return <LoadingSplash />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAgent && !isAdmin) return <Navigate to="/" replace />;
  return children;
};
